import {
  SignUpData,
  SignUpResponse,
  SignInResponse,
  SamlCheckResponse,
  AcceptTermsData,
  DropboxManagedCheckResponse,
} from 'js/sign-components/generated/types/WebApp';
import {
  validateSignUpData,
  validateAcceptTermsData,
  validateSignInResponse,
} from 'js/sign-components/generated/validators/validateWebApp';
import { assertDataIsValid } from 'signer-app/utils/ajv-validation';
import { identity } from 'lodash';
import { getSupportEmailAddress } from 'signer-app/utils/brand';
import { MockUserName } from 'signer-app/signer-app-client/mock-signer-app-client';
import { assertDataSchemaResponse } from 'signer-app/utils/response-helpers';
import * as authentication from 'signer-app/context/signer-app-client/authentication';

type Authentication = typeof authentication;

const dataUrl = (str: string) =>
  `data:,${encodeURIComponent(
    str
      .trim()
      .split('\n')
      .map((s) => s.trim())
      .join('\n'),
  )}`;

export default class MockAuthentication implements Authentication {
  mockUserName: MockUserName;

  // Allow mock app actions to pass in the mock user name
  // into this mock authentication class so that we can
  // modify functionality based on the mock username.
  //
  // eslint-disable-next-line no-useless-constructor
  constructor(mockUserName: MockUserName) {
    this.mockUserName = mockUserName;
  }

  getSignupData = async (): Promise<SignUpData> => {
    const data: SignUpData = {
      tosUrl: dataUrl('Placeholder TOS'),
      privacyPolicyUrl: dataUrl('Placeholder privacy policy'),
      arkoseSignUpPublicKey: '',
      arkoseSignInPublicKey: '',
      arkoseEnabled: false,
      emailAddress: 'member@example.com',
      firstName: null,
      lastName: null,
      googleSignInClientId: '',
      isOfficeAddIn: false,
      logInUrl: '/account/logIn',
      // The schema just specifies this as a string, but it should be a
      // CountryCode.
      initialCountryCode: 'US',
      signupSource: '',
      allowSamlLogin: true,
      planData: {
        id: 'test',
        name: 'Essentials',
        is_api: false,
        is_team_plan: false,
        num_templates: 5,
        num_sig_requests: null,
        features: {
          audit_trail: false,
          data_validation: true,
          template_links: false,
          branding: false,
          advanced_signer_fields: true,
          reporting: false,
          signer_attachments: false,
          bulk_send: false,
          embedded_signing: false,
          forms: false,
        },
        nextPlan: {
          name: 'Standard',
          id: 'test',
        },
      },
    };
    if (this.mockUserName === 'Free') {
      data.initialCountryCode = 'CA';
    }

    // Add team invite data if mock user is LaurenIpsum.
    // Lauren Ipsum will also be from France.
    // This will allow us to test team join requests.
    if (this.mockUserName === 'LaurenIpsum') {
      data.initialCountryCode = 'FR'; // France
      data.teamInvite = {
        senderEmail: 'admin@example.com',
        teamGuid: 'abc123',
        teamName: 'Foobar',
      };
    }
    assertDataIsValid(validateSignUpData, data);
    return data;
  };

  getTermsData = async (): Promise<AcceptTermsData> => {
    const data: AcceptTermsData = {
      emailAddress: 'lauren.ipsum@example.com',
      firstName: 'Lauren',
      lastName: 'Ipsum',
    };

    assertDataIsValid(validateAcceptTermsData, data);
    return data;
  };

  signUp = async (signUpArgs: Parameters<typeof authentication.signUp>[0]) => {
    const { emailAddress } = signUpArgs;
    if (emailAddress.toLocaleLowerCase() === 'exists@example.com') {
      return identity<SignUpResponse>({
        error: {
          code: 'G',
          message: 'Account already exists.',
        },
        notification: {
          message: 'You already have an account. Please log in.',
          type: 'info',
        },
        redirect_url: '/account/logIn',
        success: false,
      });
    }

    if (window.IS_STORYBOOK) {
      return identity<SignUpResponse>({
        success: true,
        redirect_url: dataUrl(`SUCCESS!

        This page was generated to represent where the user is sent next.
        In the designs I'm looking at that is /info/chooseSignUpPlan

        signupArgs:
        ${JSON.stringify(signUpArgs, null, 2)}
        `),
      });
    }

    // Redirect to onboarding if user has signed up via
    // team invite.
    if (signUpArgs.emailAddress === 'member@example.com') {
      return identity<SignUpResponse>({
        success: true,
        redirect_url: '/onboarding/setpassword',
      });
    }

    if (signUpArgs.emailAddress === 'testupsell@example.com') {
      return identity<SignUpResponse>({
        success: true,
        redirect_url: '/account/emailActivation',
      });
    }

    return identity<SignUpResponse>({
      success: true,
      redirect_url: '/info/chooseSignUpPlan',
    });
  };

  samlCheck = async (
    samlCheckArgs: Parameters<typeof authentication.samlCheck>[0],
  ) => {
    const { emailAddress } = samlCheckArgs;

    if (window.IS_STORYBOOK) {
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    // Special case: disable arkose
    if (emailAddress.toLocaleLowerCase() === 'arkosedisabled@example.com') {
      return identity<SamlCheckResponse>({
        saml: false,
        login_url: '',
        disable_arkose: true,
      });
    }

    // Special case: enable SAML
    if (emailAddress.toLocaleLowerCase() === 'saml@example.com') {
      if (window.IS_STORYBOOK) {
        return identity<SamlCheckResponse>({
          saml: true,
          login_url: dataUrl(`SUCCESS!

          This page was generated to represent where the user is sent next.

          samlCheckArgs:
          ${JSON.stringify(samlCheckArgs, null, 2)}
          `),
          disable_arkose: false,
        });
      }

      return identity<SamlCheckResponse>({
        saml: true,
        login_url: 'https://example.com/samlLogin',
        disable_arkose: false,
      });
    }

    // Special case: SAML available, but password login is also allowed.
    if (emailAddress.toLocaleLowerCase() === 'samladmin@example.com') {
      return identity<SamlCheckResponse>({
        saml: false,
        login_url: 'https://example.com/samlLogin',
        disable_arkose: false,
      });
    }

    return identity<SamlCheckResponse>({
      saml: false,
      login_url: '',
      disable_arkose: false,
    });
  };

  dropboxManagedCheck = async (
    dropboxManagedCheckArgs: Parameters<
      typeof authentication.dropboxManagedCheck
    >[0],
  ) => {
    const { emailAddress } = dropboxManagedCheckArgs;

    // Dropbox Managed Account
    if (emailAddress.toLocaleLowerCase() === 'dma@example.com') {
      return identity<DropboxManagedCheckResponse>({
        error: {
          code: 'O',
          message:
            'Looks like there’s already a Dropbox account associated with this email.',
        },
      });
    }

    return identity<DropboxManagedCheckResponse>({});
  };

  signIn = async (signInArgs: Parameters<typeof authentication.signIn>[0]) => {
    const { emailAddress } = signInArgs;

    if (window.IS_STORYBOOK) {
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    // Special case: invalid username/password combo.
    if (emailAddress.toLocaleLowerCase() === 'invalid@example.com') {
      return identity<SignInResponse>({
        success: false,
        error: {
          code: 'A',
          message: 'Invalid username/password combo',
        },
      });
    }

    // Special case: blocked email address
    if (emailAddress.toLocaleLowerCase() === 'blocked@example.com') {
      return identity<SignInResponse>({
        success: false,
        error: {
          code: 'C',
          message: `Your email address has been blocked. Please contact us at ${getSupportEmailAddress(
            'S',
          )}`,
        },
      });
    }

    // Special case: blocked IP address
    if (emailAddress.toLocaleLowerCase() === 'blockedip@example.com') {
      return identity<SignInResponse>({
        success: false,
        error: {
          code: 'D',
          message: `Your IP address has been blocked. Please contact us at ${getSupportEmailAddress(
            'S',
          )}`,
        },
      });
    }

    // Special case: blocked country
    if (emailAddress.toLocaleLowerCase() === 'blockedcountry@example.com') {
      return identity<SignInResponse>({
        success: false,
        error: {
          code: 'Y',
          message: 'We do not support authentication from your location',
        },
      });
    }

    // Special case: locked account
    if (emailAddress.toLocaleLowerCase() === 'locked@example.com') {
      return identity<SignInResponse>({
        success: false,
        error: {
          code: 'N',
          message:
            'Your account has been locked out by the administrator. Please contact your administrator to have your account unlocked',
        },
      });
    }

    // Special case: rate limited account; temporarily locked
    if (emailAddress.toLocaleLowerCase() === 'ratelimited@example.com') {
      return identity<SignInResponse>({
        success: false,
        error: {
          code: 'R',
          message:
            'This account has been locked due to excessive failed login attempts, please try again in 15 minutes',
        },
      });
    }

    // Special case: csrf attack
    if (emailAddress.toLocaleLowerCase() === 'csrfattack@example.com') {
      return identity<SignInResponse>({
        success: false,
        error: {
          code: 'X',
          message:
            'There was an error logging in. Please refresh the page and try again',
        },
      });
    }

    // Special case: arkose verification failed
    if (emailAddress.toLocaleLowerCase() === 'arkose@example.com') {
      return identity<SignInResponse>({
        success: false,
        error: {
          code: 'Z',
          message: 'Verification test failed. Please try again.',
        },
      });
    }

    // Special case: attempted to log in to OpenId account via username/password
    if (emailAddress.toLocaleLowerCase() === 'openid@example.com') {
      return identity<SignInResponse>({
        success: false,
        error: {
          code: 'E',
          message:
            'Either the credentials you supplied are invalid, or you signed up using an OpenID provider, such as Google',
        },
      });
    }

    // Special case: unknown error
    if (emailAddress.toLocaleLowerCase() === 'error@example.com') {
      return identity<SignInResponse>({
        success: false,
        error: {
          code: 'K',
          message: 'There was an error logging in. Please try again',
        },
      });
    }

    // Special case: unknown error from validation error
    if (emailAddress.toLocaleLowerCase() === 'baddata@example.com') {
      return assertDataSchemaResponse(
        {
          bad: 'data',
        } as any as Response,
        validateSignInResponse,
      );
    }

    if (window.IS_STORYBOOK) {
      return identity<SignInResponse>({
        success: true,
        on_login_redirect_url: dataUrl(`SUCCESS!

        This page was generated to represent where the user is sent next.

        signInArgs:
        ${JSON.stringify(signInArgs, null, 2)}
        `),
      });
    }

    return identity<SignInResponse>({
      success: true,
    });
  };
}
